import React, { useState } from "react";
import useSWR from "swr";
import LockIcon from "../../../../svgs/shopping-bag.svg";
import UserIcon from "../../../../svgs/card.svg";
import BuildingIcon from "../../../../svgs/building.svg";
import PlusIcon from "../../../../svgs/add-pink.svg";
import CloseIcon from "../../../../svgs/close.svg";
import TypeIcon from "../../../../svgs/type.svg";
import DocumentIcon from "../../../../svgs/document-upload.svg";
import DocumentUpload from "../../../Common/DocumentUpload";
import { useNotification } from "../../../../../Notifications/NotificationProvider";
import {
  inventoryFetcher,
  InventoryService,
} from "../../../../network/InventoryService";
import FormField from "../../../../../Shared/Form/FormField";

const INVENTORY_TYPE_OPTIONS = [
  {
    value: "",
    label: "Select an inventory type Device/Sim/Other...",
    isPlaceholder: true,
  },
  { value: "device", label: "Device" },
  { value: "sim", label: "Sim" },
  { value: "other", label: "Other" },
];

const POCreateModal = ({
  showPOCreateModal,
  setShowPOCreateModal,
  onPOCreated,
}) => {
  const dispatchNotification = useNotification();
  const [form, setForm] = useState({
    attachment: null,
    poNumber: "",
    initiator: "",
    description: "",
    supplier: "",
    inventoryType: "",
    devices: [
      {
        item: "",
        quantity: "",
        unitCost: "",
        totalCost: "",
      },
    ],
  });
  const [formError, setFormError] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [hasSelectedInventoryType, setHasSelectedInventoryType] =
    useState(false);

  const dropdownParams = new URLSearchParams({
    is_dropdown: true,
  });

  const { data: devicesResponse, error: devicesError } = useSWR(
    `${
      process.env.REACT_APP_API_URL
    }/api/payment_speaker/v1/psb-model?${dropdownParams.toString()}`,
    inventoryFetcher,
    {
      fallbackData: {},
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  const devices = devicesResponse?.data
    ? devicesResponse?.data.filter(
        (device) => device.type === form.inventoryType
      )
    : [];
  const devicesLoading = !devicesResponse && !devicesError;
  const deviceOptions = [
    {
      value: "",
      label: devicesLoading
        ? "Loading devices..."
        : devicesError
          ? "Error loading devices"
          : "Select a device...",
      isPlaceholder: true,
    },
    ...devices.map((device) => ({
      value: device.id.toString(),
      label: `${device.name} - ${device.brand} ${device.model}`,
    })),
  ];
  const handleDeviceChange = (idx, field, value) => {
    const devices = [...form.devices];
    devices[idx][field] = value;
    if (field === "quantity" || field === "unitCost") {
      const qty = parseInt(devices[idx].quantity || "0", 10);
      const cost = parseInt(devices[idx].unitCost || "0", 10);
      devices[idx].totalCost = qty && cost ? (qty * cost).toLocaleString() : "";
    }
    setForm({ ...form, devices });
  };

  const handleAddDevice = () => {
    setForm({
      ...form,
      devices: [
        ...form.devices,
        {
          item: "",
          quantity: "",
          unitCost: "",
          totalCost: "",
        },
      ],
    });
  };

  const handleRemoveDevice = (idx) => {
    const devices = form.devices.filter((_, i) => i !== idx);
    setForm({ ...form, devices });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "inventoryType") {
      if (hasSelectedInventoryType && value !== form.inventoryType) {
        setForm({
          ...form,
          attachment: null,
          poNumber: "",
          initiator: "",
          description: "",
          supplier: "",
          [name]: value,
          devices: [
            {
              item: "",
              quantity: "",
              unitCost: "",
              totalCost: "",
            },
          ],
        });
      } else {
        setForm({ ...form, [name]: value });
        if (value && !hasSelectedInventoryType) {
          setHasSelectedInventoryType(true);
        }
      }
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleAttachmentChange = (e) => {
    setForm({ ...form, attachment: e.target.files[0] });
  };

  const handleDeleteAttachment = () => {
    setForm({ ...form, attachment: null });
  };

  const handleSupplierChange = (e) => {
    setForm({ ...form, supplier: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.poNumber) {
      setFormError("Please fill up PO Number");
      return;
    }

    if (!form.initiator) {
      setFormError("Please fill up Initiator");
      return;
    }

    if (!form.inventoryType) {
      setFormError("Please select an Inventory Type");
      return;
    }

    if (!form.supplier) {
      setFormError("Please select a Supplier");
      return;
    }

    if (!form.attachment) {
      setFormError("Please upload PO Attachment");
      return;
    }

    const hasValidDevices = form.devices.every(
      (device) => device.item && device.quantity && device.unitCost
    );

    if (!hasValidDevices) {
      setFormError("Please complete all device information.");
      return;
    }

    setIsCreating(true);
    setFormError("");

    try {
      const poRequestData = {
        po_number: form.poNumber,
        po_initiator: form.initiator,
        model_type: form.inventoryType,
        supplier: form.supplier,
        description: form.description,
        attachment: form.attachment,
        device_info: form.devices.map((device) => ({
          model_id: device.item,
          model_name:
            deviceOptions.find((opt) => opt.value === device.item)?.label || "",
          quantity: parseInt(device.quantity, 10),
          unit_cost: parseInt(device.unitCost, 10),
          total: parseInt(device.totalCost.replace(/,/g, "") || "0", 10),
        })),
      };

      const response = await InventoryService.createPO(poRequestData);

      if (response.is_error === 0) {
        dispatchNotification({
          type: "SUCCESS",
          message: "PO created successfully!",
        });

        setForm({
          attachment: null,
          poNumber: "",
          initiator: "",
          description: "",
          supplier: "",
          inventoryType: "",
          devices: [
            {
              item: "",
              quantity: "",
              unitCost: "",
              totalCost: "",
            },
          ],
        });

        setShowPOCreateModal(false);

        if (onPOCreated) {
          onPOCreated();
        }
      } else {
        throw new Error(response.message || "Failed to create PO");
      }
    } catch (error) {
      console.error("Error creating PO:", error);

      let errorMessage = "Failed to create PO. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setFormError(errorMessage);

      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const { data: suppliersResponse, error: suppliersError } = useSWR(
    form.inventoryType
      ? `${
          process.env.REACT_APP_API_URL
        }/api/payment_speaker/v1/psb-supplier?${dropdownParams.toString()}&type=${
          form.inventoryType
        }`
      : null,
    inventoryFetcher,
    {
      fallbackData: {},
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  const suppliers = suppliersResponse?.data || [];
  const suppliersLoading = !suppliersResponse && !suppliersError;

  const supplierOptions = [
    {
      value: "",
      label: suppliersLoading
        ? "Loading suppliers..."
        : suppliersError
          ? "Error loading suppliers"
          : "Select a supplier...",
      isPlaceholder: true,
    },
    ...suppliers.map((supplier) => ({
      value: supplier.id.toString(),
      label: `${supplier.name} (${supplier.unique_id || supplier.id})`,
    })),
  ];

  if (!showPOCreateModal) return null;

  return (
    <div
      className="inventory-modal-overlay"
      onClick={() => !isCreating && setShowPOCreateModal(false)}
    >
      <form
        className="inventory-modal po-create-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="inventory-modal-header">
          <h3>PO Creation Form</h3>
          <button
            className="close-btn"
            type="button"
            onClick={() => !isCreating && setShowPOCreateModal(false)}
            disabled={isCreating}
          >
            ×
          </button>
        </div>
        <div className="inventory-modal-body">
          <div className="inventory-modal-body-content">
            <div className="po-info-section">
              <h2>PO Information</h2>
              <DocumentUpload
                attachment={form.attachment}
                handleAttachmentChange={handleAttachmentChange}
                uploadTitle="Upload PO Attachment"
                downloadTitle="Download PO Attachment"
                documentIcon={DocumentIcon}
                downloadMode={false}
                setAttachment={handleDeleteAttachment}
              />
              <div className="inventory-form-container">
                <FormField
                  id="po-number"
                  name="poNumber"
                  title="PO Number"
                  type="text"
                  icon={LockIcon}
                  value={form.poNumber}
                  onChange={handleInputChange}
                  placeholder="Enter PO number..."
                  disabled={isCreating}
                  required
                />
                <FormField
                  id="initiator"
                  name="initiator"
                  title="PO Initiator"
                  icon={UserIcon}
                  type="text"
                  value={form.initiator}
                  onChange={handleInputChange}
                  placeholder="Enter PO initiator..."
                  disabled={isCreating}
                  required
                />
                <FormField
                  id="inventoryType"
                  name="inventoryType"
                  title="Inventory Type"
                  type="select"
                  icon={TypeIcon}
                  options={INVENTORY_TYPE_OPTIONS}
                  value={form.inventoryType}
                  onChange={handleInputChange}
                  disabled={isCreating}
                  isFlat
                  required
                />
                <FormField
                  id="description"
                  name="description"
                  title="PO Description"
                  type="textarea"
                  value={form.description}
                  onChange={handleInputChange}
                  placeholder="Enter PO description..."
                  disabled={isCreating}
                  required
                />
              </div>
            </div>
            <div className="supplier-device-section">
              <h2>Supplier Information</h2>
              <div className="inventory-form-container po-supplier-form">
                <FormField
                  id="supplier"
                  name="supplier"
                  title="Select Supplier"
                  type="select"
                  icon={BuildingIcon}
                  value={form.supplier}
                  onChange={handleSupplierChange}
                  options={supplierOptions}
                  disabled={isCreating || suppliersLoading || suppliersError}
                  required
                  isFlat
                />
              </div>
              <h2>Device Information</h2>
              <div className="device-box">
                <div className="total-cost">
                  Total Cost &nbsp;
                  <span>
                    BDT&nbsp;
                    {form.devices
                      .reduce(
                        (sum, d) =>
                          sum +
                          parseInt(d.totalCost.replace(/,/g, "") || "0", 10),
                        0
                      )
                      .toLocaleString()}
                    .00
                  </span>
                </div>
                <div className="device-header">
                  <div className="device-field">Item</div>
                  <div className="device-field">Quantity</div>
                  <div className="device-field">Unit Cost (BDT)</div>
                  <div className="device-field">Total Cost (BDT)</div>
                  <div className="device-field"></div>
                </div>
                <div className="device-rows">
                  {form.devices.map((device, idx) => (
                    <div className="device-row" key={idx}>
                      <div className="device-field">
                        <select
                          value={device.item}
                          onChange={(e) =>
                            handleDeviceChange(idx, "item", e.target.value)
                          }
                          className="form-input select-input"
                          disabled={isCreating}
                          required
                        >
                          {deviceOptions.map((opt) => (
                            <option
                              key={opt.value}
                              value={opt.value}
                              disabled={opt.isPlaceholder}
                            >
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="device-field">
                        <input
                          type="number"
                          min="1"
                          value={device.quantity}
                          onChange={(e) =>
                            handleDeviceChange(idx, "quantity", e.target.value)
                          }
                          className="form-input"
                          placeholder="Quantity"
                          disabled={isCreating}
                          required
                        />
                      </div>
                      <div className="device-field">
                        <input
                          type="number"
                          min="1"
                          value={device.unitCost}
                          onChange={(e) =>
                            handleDeviceChange(idx, "unitCost", e.target.value)
                          }
                          className="form-input"
                          placeholder="Unit Cost"
                          disabled={isCreating}
                          required
                        />
                      </div>
                      <div className="device-field">
                        <input
                          type="text"
                          value={device.totalCost}
                          className="form-input"
                          readOnly
                          placeholder="Total Cost"
                        />
                      </div>
                      <div className="device-field device-remove">
                        {form.devices.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveDevice(idx)}
                            className="remove-device-btn"
                            disabled={isCreating}
                            tabIndex={-1}
                          >
                            <img
                              src={CloseIcon}
                              alt="Remove"
                              className="remove-device-icon"
                            />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={handleAddDevice}
                    className="add-device-btn"
                    disabled={isCreating}
                  >
                    <img src={PlusIcon} alt="Add" className="add-device-icon" />
                    Add info for another device
                  </button>
                </div>
              </div>
            </div>
          </div>

          {formError && <div className="inventory-form-error">{formError}</div>}
        </div>
        <div className="inventory-modal-footer">
          <button
            className="cancel-btn"
            type="button"
            onClick={() => setShowPOCreateModal(false)}
            disabled={isCreating}
          >
            Cancel
          </button>
          <button className="submit-btn" type="submit" disabled={isCreating}>
            {isCreating ? "Saving..." : "Save PO Information"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default POCreateModal;
